import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";

import { MessageFileStore } from "../../api/fileStore.type";
import { createGroupChatMessage, createMessage } from "../../api/message.api";
import { MessageRole, MessageRow } from "../../api/message.type";
import { useFileContext } from "../FileContextWrapper";
import { SyncMessagesFn } from "./useMessageSync";

interface UseMessageMutationsOptions {
  isGroupChat: boolean;
  groupChatId?: string;
  userId?: string;
  syncMessages: SyncMessagesFn;
  setIsTyping: (value: boolean) => void;
  setPendingMessage: (message: MessageRow | null) => void;
  startStreaming: (messageId: string) => void;
  text: string;
  setText: (value: string) => void;
}

export function useMessageMutations({
  isGroupChat,
  groupChatId,
  userId,
  syncMessages,
  setIsTyping,
  setPendingMessage,
  startStreaming,
  text,
  setText,
}: UseMessageMutationsOptions) {
  const { fileIds, pendingImages, clearPendingImages, setImageUploadError } =
    useFileContext();

  const { mutate: submitRegularMessage } = useMutation({
    mutationFn: createMessage,
    onSuccess: (res) => {
      setPendingMessage(null);
      clearPendingImages();

      syncMessages((prev) => {
        const withoutExisting = prev.filter((msg) => msg.id !== res.id);
        const added = withoutExisting.length === prev.length;
        return {
          next: [res, ...withoutExisting],
          totalCountDelta: added ? 1 : 0,
        };
      });

      startStreaming(res.id);
    },
    onError: (error) => {
      console.warn("submitRegularMessage_failed", error);
      setIsTyping(false);
      setImageUploadError("Failed to send message. Please try again.");
    },
  });

  const { mutate: submitGroupMessage } = useMutation({
    mutationFn: createGroupChatMessage,
    onSuccess: (res: MessageRow) => {
      const isStreaming = res?.content?.includes("@harper");

      setPendingMessage(null);
      clearPendingImages();

      syncMessages((prev) => {
        const withoutExisting = prev.filter((msg) => msg.id !== res.id);
        const added = withoutExisting.length === prev.length;
        return {
          next: [res, ...withoutExisting],
          totalCountDelta: added ? 1 : 0,
        };
      });

      if (isStreaming) {
        startStreaming(res.id);
      } else {
        setIsTyping(false);
      }
    },
    onError: (error) => {
      console.warn("submitGroupMessage_failed", error);
      setIsTyping(false);
      setImageUploadError("Failed to send message. Please try again.");
    },
  });

  const sendMessageToApi = useCallback(
    async (content: string) => {
      setIsTyping(true);
      setImageUploadError(null);

      if (isGroupChat) {
        if (!groupChatId) {
          console.warn("sendMessageToApi_missing_group_chat_id");
          setIsTyping(false);
          return;
        }
        submitGroupMessage({
          groupChatId,
          content,
          fileStoreIds: fileIds,
        });
      } else {
        submitRegularMessage({
          userId: userId || "",
          content,
          ...(fileIds.length > 0 && { fileStoreIds: fileIds }),
        });
      }
    },
    [
      fileIds,
      groupChatId,
      isGroupChat,
      setIsTyping,
      submitGroupMessage,
      submitRegularMessage,
      userId,
    ]
  );

  const sendMessage = useCallback(
    async (overrideContent?: string) => {
      const messageContent = overrideContent ?? text;
      if (!messageContent && fileIds.length === 0) return;

      const filePurpose = isGroupChat ? "group_chat" : "chat_message";

      const mockFileStores: MessageFileStore[] = fileIds.map(
        (fileId, index) => ({
          id: `pending-${index}`,
          message_id: new Date().valueOf().toString(),
          file_store_id: fileId,
          file_store: {
            id: fileId,
            url: pendingImages[index] || "",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: userId || "",
            purpose: filePurpose,
            name: `image-${index + 1}.jpg`,
            ambient_conversation_id: null,
            audio_transcript: null,
            file_id: null,
            notes: null,
            project_id: null,
            relation_id: null,
            type: null,
            vector_store_id: null,
          },
        })
      );

      const newMessage: MessageRow = {
        id: new Date().valueOf().toString(),
        content: messageContent,
        created_at: new Date().toISOString(),
        role: MessageRole.USER,
        user_id: userId || "",
        ...(isGroupChat && { group_chat_id: groupChatId }),
        message_file_store: mockFileStores,
        agent_id: null,
        generated_by: null,
        project_id: null,
        response_id: null,
        run_id: null,
        thread_id: null,
        is_deleted: null,
        message_type: null,
        reply_to_message_id: null,
        deleted_at: null,
        user: {
          id: userId || "",
        },
      } as MessageRow;

      setPendingMessage(newMessage);

      await sendMessageToApi(messageContent);
      if (!overrideContent) {
        setText("");
      }
    },
    [
      fileIds,
      groupChatId,
      isGroupChat,
      pendingImages,
      sendMessageToApi,
      setPendingMessage,
      setText,
      text,
      userId,
    ]
  );

  return {
    sendMessage,
    sendMessageToApi,
    clearPendingImages,
  } as const;
}
